import { useEffect } from "react";

interface DocumentMeta {
  title: string;
  description: string;
  canonical: string;
  robots?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  schema?: Record<string, unknown>;
}

const HOME_META = {
  title: "Chris Workflows. AI-Native GTM & Growth Strategy",
  description: "Chris Scibiorek. Strategy / GTM Lead and AI workflow builder. 10 years in Web3, 30+ projects from accelerator to listing. Available now for senior strategy roles.",
  canonical: "https://chrisworkflows.com/",
  ogUrl: "https://chrisworkflows.com",
};

// Strip trailing slash for hreflang URL consistency. Canonical may end with `/`
// (Gateway root) or not (other pages); hreflang must be consistent across all
// pages so Google does not treat /pl and /pl/ as separate alternates.
function stripTrailingSlash(url: string): string {
  if (url === "https://chrisworkflows.com/") return "https://chrisworkflows.com";
  return url.replace(/\/$/, "");
}

function setOrCreateHreflang(hreflang: string, href: string) {
  let el = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
}

export function useDocumentMeta({ title, description, canonical, robots, ogTitle, ogDescription, ogImage, schema }: DocumentMeta) {
  useEffect(() => {
    const getMeta = (sel: string) => document.querySelector(sel) as HTMLMetaElement | null;
    const getLink = (sel: string) => document.querySelector(sel) as HTMLLinkElement | null;

    // Title
    document.title = title;

    // Description
    getMeta('meta[name="description"]')?.setAttribute("content", description);

    // Canonical
    getLink('link[rel="canonical"]')?.setAttribute("href", canonical);

    // Robots: inject or update
    let robotsEl = getMeta('meta[name="robots"]');
    const robotsInjected = !robotsEl && !!robots;
    if (robots) {
      if (!robotsEl) {
        robotsEl = document.createElement("meta");
        robotsEl.setAttribute("name", "robots");
        document.head.appendChild(robotsEl);
      }
      robotsEl.setAttribute("content", robots);
    }

    // Hreflang: derive from canonical (EN, PL, x-default), normalized without trailing slash
    let enEl: HTMLLinkElement | null = null;
    let plEl: HTMLLinkElement | null = null;
    let xDefaultEl: HTMLLinkElement | null = null;
    const isPlPage = canonical.includes("/pl");
    const enUrlRaw = isPlPage
      ? canonical.replace("https://chrisworkflows.com/pl", "https://chrisworkflows.com") || "https://chrisworkflows.com"
      : canonical;
    const plUrlRaw = isPlPage
      ? canonical
      : canonical.replace("https://chrisworkflows.com", "https://chrisworkflows.com/pl");
    const enUrl = stripTrailingSlash(enUrlRaw);
    const plUrl = stripTrailingSlash(plUrlRaw);
    enEl = setOrCreateHreflang("en", enUrl);
    plEl = setOrCreateHreflang("pl", plUrl);
    xDefaultEl = setOrCreateHreflang("x-default", enUrl);

    // OG
    getMeta('meta[property="og:title"]')?.setAttribute("content", ogTitle ?? title);
    getMeta('meta[property="og:description"]')?.setAttribute("content", ogDescription ?? description);
    getMeta('meta[property="og:url"]')?.setAttribute("content", canonical);
    if (ogImage) getMeta('meta[property="og:image"]')?.setAttribute("content", ogImage);

    // Twitter
    getMeta('meta[name="twitter:title"]')?.setAttribute("content", ogTitle ?? title);
    getMeta('meta[name="twitter:description"]')?.setAttribute("content", ogDescription ?? description);

    // Per-page JSON-LD schema (optional)
    let schemaEl: HTMLScriptElement | null = null;
    if (schema) {
      schemaEl = document.createElement("script");
      schemaEl.setAttribute("type", "application/ld+json");
      schemaEl.setAttribute("data-page-schema", "true");
      schemaEl.textContent = JSON.stringify(schema);
      document.head.appendChild(schemaEl);
    }

    return () => {
      // Restore home defaults on unmount
      document.title = HOME_META.title;
      getMeta('meta[name="description"]')?.setAttribute("content", HOME_META.description);
      getLink('link[rel="canonical"]')?.setAttribute("href", HOME_META.canonical);
      getMeta('meta[property="og:title"]')?.setAttribute("content", HOME_META.title);
      getMeta('meta[property="og:description"]')?.setAttribute("content", HOME_META.description);
      getMeta('meta[property="og:url"]')?.setAttribute("content", HOME_META.ogUrl);
      getMeta('meta[name="twitter:title"]')?.setAttribute("content", HOME_META.title);
      getMeta('meta[name="twitter:description"]')?.setAttribute("content", HOME_META.description);
      // Remove injected robots tag
      if (robotsInjected && robotsEl && robotsEl.parentNode) {
        robotsEl.parentNode.removeChild(robotsEl);
      }
      // Remove injected hreflang tags
      if (enEl && enEl.parentNode) enEl.parentNode.removeChild(enEl);
      if (plEl && plEl.parentNode) plEl.parentNode.removeChild(plEl);
      if (xDefaultEl && xDefaultEl.parentNode) xDefaultEl.parentNode.removeChild(xDefaultEl);
      // Remove per-page schema
      if (schemaEl && schemaEl.parentNode) schemaEl.parentNode.removeChild(schemaEl);
    };
  }, [title, description, canonical, robots, ogTitle, ogDescription, ogImage, schema]);
}
